import React, { useEffect } from 'react';
import { useContentstackPage } from '../hooks/useContentstack';
import { CmsComponentRenderer } from '../components/CmsComponentRenderer/CmsComponentRenderer';
import UnauthorizedHeader from './UnauthorizedHeader';
import { Navbar } from '../components/layout/Navbar';
import { CmsButton } from '../types/contentstack';
import { PageNotFound } from './error/page-not-found';
import { useAPI } from '../hooks/useAPI';
import { useShelter } from '../hooks/useShelter';
import { WelcomeFlowTracking } from '../services/analytics';
import { useTranslation } from '../hooks/useTranslation';
import { TranslationKeyEnum } from '../types/constants';

interface DynamicPageProps {
  slug?: string;
  authenticatedSlug?: string;
  header?: boolean;
  onFooterExtracted?: (footerButtons: CmsButton[]) => void;
  onClose?: () => void;
  welcomeFlowTracking?: WelcomeFlowTracking;
}

const DynamicPage: React.FC<DynamicPageProps> = ({ slug, authenticatedSlug, header = true, onFooterExtracted, onClose, welcomeFlowTracking }) => {
  const { isAuthenticated, isInitialized } = useAPI();
  const { shelter } = useShelter();
  const { t } = useTranslation();
  const resolvedSlug = (authenticatedSlug && isAuthenticated ? authenticatedSlug : slug) as string;
  const { data: pageData, loading, error } = useContentstackPage(resolvedSlug);
  useEffect(() => {
    if (pageData) {
      document.title = pageData.seo?.meta_title ?? pageData.title ?? 'Food Shelter Love';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && pageData.seo?.meta_description) {
        metaDescription.setAttribute('content', pageData.seo.meta_description);
      } else if (pageData.seo?.meta_description) {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = pageData.seo.meta_description;
        document.head.appendChild(meta);
      }

      if (onFooterExtracted && pageData.components) {
        const lastComponent = pageData.components[pageData.components.length - 1];
        if (lastComponent) {
          const componentKey = Object.keys(lastComponent)[0];
          const componentData = (lastComponent as Record<string, Record<string, unknown>>)[
            componentKey
          ];
          const buttons = componentData?.buttons;
          if (Array.isArray(buttons) && buttons.length > 0) {
            onFooterExtracted(buttons as CmsButton[]);
          }
        }
      }
    }
  }, [pageData, onFooterExtracted]);

  if (loading || (header && !isInitialized)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t(TranslationKeyEnum.LoadingPage)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('[DynamicPage] Error loading page:', error);
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-2">{t(TranslationKeyEnum.ErrorLoadingPage)}</h2>
          <p className="text-red-600">{error.message ?? t(TranslationKeyEnum.FailedToLoadPageContent)}</p>
        </div>
      </div>
    );
  }

  if (!pageData) {
    // If this is a popup/modal (onClose is provided), return nothing
    // since contentstack data is optional and may not exist
    if (onClose) {
      return null;
    }
    // For full pages, show the 404 page
    return <PageNotFound />;
  }

  return (
    <div className="dynamic-page font-montserrat">
      {header && isInitialized &&
        (isAuthenticated ? <div className="px-500"><Navbar shelterName={shelter?.name} /></div> : <UnauthorizedHeader />)}
      <CmsComponentRenderer
        components={pageData.components ?? []}
        onClose={onClose}
        welcomeFlowTracking={welcomeFlowTracking}
      />
    </div>
  );
};

export default DynamicPage;
